import { useEffect, useState } from "react";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { LineChart, Line, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Award } from "lucide-react";

function Metric({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">{label}</div>
      <div className="mt-3 font-display text-4xl font-light tabnum">{value}</div>
      {sub && <div className="mt-1 text-sm text-obsidian/50">{sub}</div>}
    </div>
  );
}

function MiniChart({ data, dataKey, xKey }) {
  if (!data || data.length < 2) return <div className="h-24 flex items-center text-xs text-obsidian/30">Pas assez de données</div>;
  return (
    <ResponsiveContainer width="100%" height={100}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "#999" }} axisLine={{ stroke: "#E5E5E0" }} tickLine={false} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E5E0" }} />
        <Line type="monotone" dataKey={dataKey} stroke="#8C9C90" strokeWidth={2} dot={{ r: 3, fill: "#8C9C90" }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function Evolution() {
  const [d, setD] = useState(null);
  const [records, setRecords] = useState([]);
  const load = () => {
    api.get("/progression").then((r) => setD(r.data))
      .catch(() => setD({ performance: { runs: [] }, regularity: { rate: 0, planned: 0, done: 0, missed: 0 }, body: { entries: [] }, feeling: { fatigue_series: [], difficulty_series: [] }, ai_context: "" }));
    api.get("/records").then((r) => setRecords(r.data.records || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!d) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;

  const bodyData = (d.body.entries || []).map((e, i) => ({ x: `M${i + 1}`, poids: e.poids, taille: e.tour_taille }));
  const feelData = d.feeling.fatigue_series.map((f, i) => ({ x: `S${i + 1}`, fatigue: f, difficulté: d.feeling.difficulty_series[i] }));

  return (
    <ClientLayout>
      <div className="fade-up">
        <h1 className="text-3xl font-light tracking-tight">Mon évolution</h1>
        {d.ai_context && (
          <div className="mt-4 rounded-xl bg-sage/8 border border-sage/20 p-5">
            <ProvenanceBadge type="analyse" className="mb-3" />
            <p className="text-sm text-obsidian/80 leading-relaxed">{d.ai_context}</p>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Metric label="Performance" value={`${d.performance.sessions_done}/${d.performance.sessions_planned}`} sub="séances réalisées" />
          <Metric label="Régularité" value={`${Math.round(d.regularity.rate * 100)}%`} sub={`${d.regularity.done} réal. · ${d.regularity.missed} manq.`} />
        </div>

        <div className="mt-4 rounded-xl border border-border bg-white p-6">
          <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-2">Corps</div>
          <MiniChart data={bodyData} dataKey="poids" xKey="x" />
          <div className="mt-2 text-xs text-obsidian/40">Poids (kg) dans le temps</div>
        </div>

        <div className="mt-4 rounded-xl border border-border bg-white p-6">
          <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-2">Ressenti / récupération</div>
          <MiniChart data={feelData} dataKey="fatigue" xKey="x" />
          <div className="mt-2 text-xs text-obsidian/40">Fatigue déclarée par séance</div>
        </div>

        <p className="mt-6 text-xs text-obsidian/40">L'IA évite de tirer une conclusion d'un seul chiffre isolé.</p>

        {records.length > 0 && (
          <div className="mt-10" data-testid="records-section">
            <div className="flex items-center gap-2">
              <Award size={16} strokeWidth={1.6} className="text-sage" />
              <h2 className="text-sm tracking-[0.16em] uppercase font-semibold text-obsidian/50">Accomplissements</h2>
            </div>
            <p className="mt-1 text-xs text-obsidian/40">On valorise la progression dans la vraie vie, pas l'usage de l'application.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {records.map((r, i) => (
                <div key={i} className="rounded-xl border border-border bg-white p-4 flex items-start gap-3">
                  <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />
                  <div>
                    <div className="text-sm font-medium">{r.title}</div>
                    <div className="text-xs text-obsidian/50 mt-0.5">{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
